/**
 * Pure helpers for `drag_rank` cards: `items` (stored in any order) plus `correctOrder` (ids).
 *
 * Both apps shuffle `items` with `sort(() => Math.random() - 0.5)`, which is heavily biased
 * towards the stored order — with 3 items the user is handed the stored order ~37% of the
 * time. So `items` must never be stored already solved.
 */

export interface RankItem {
  id: string;
  text: string;
  icon: string;
  [key: string]: unknown;
}

/**
 * Indexes into `items` in the correct order: those named by `correctOrder` first, then any
 * item it leaves out. Matches by position, so items sharing an id are all kept.
 */
export function rankIndexes(items: RankItem[], correctOrder: string[]): number[] {
  const placed: number[] = [];
  correctOrder.forEach((id) => {
    const index = items.findIndex((item, i) => item?.id === id && !placed.includes(i));
    if (index >= 0) placed.push(index);
  });
  return [...placed, ...items.map((_, i) => i).filter((i) => !placed.includes(i))];
}

/**
 * The card fields for `items` ranked as `ranking`: `correctOrder` from the ranking, and
 * `items` in their current stored order — reversed when that order would be the answer.
 */
export function rankedFields(items: RankItem[], ranking: number[]): { items: RankItem[]; correctOrder: string[] } {
  const correctOrder = ranking.map((i) => items[i].id);
  const solved = items.length > 1 && ranking.every((index, position) => index === position);
  return { items: solved ? items.slice().reverse() : items, correctOrder };
}
